import type { ITabOption } from '@src/components/atoms/tabs';
import Tabs from '@src/components/atoms/tabs';
import { GovernanceActionType } from '@src/models/enum';
import type { IGovernanceAction } from '@src/models/types';
import { useEffect, useState } from 'react';
import GovernanceActionReasoning from '@src/components/molecules/governance-action-detail/govActionReasoning';
import GovernanceExtraActionDetailsPage from '@src/components/molecules/governance-action-detail/govActionExtraDetails';
import { readSearchParam } from '@src/utils/searchParamsUtils';
import AnchorLink from '@src/components/atoms/links/anchor-link';
import { getFieldValue, handleNavigationUrl } from '@src/utils/metadataUtils';
import GovActionMetadataCopiableDescriptionField from '@src/components/atoms/metadataCopiableDescription';
import ContentCard from '@src/components/cards/contentCard';
import React from 'react';
import type { MetadataValidationResponse } from '@src/models/dtos/metadata';

const GovernanceActionMetadata = ({
  governanceAction,
  metadataResponse,
}: {
  governanceAction: IGovernanceAction;
  metadataResponse: MetadataValidationResponse | null;
}) => {
  const { proposal } = governanceAction;
  const { metadataHash, metadataUrl, type } = proposal;
  const metadataTabs: ITabOption[] = [
    {
      value: 'resoning',
      label: 'Reasoning',
      tabClassName: 'rounded-tl-lg min-w-[117px]',
    },
    {
      value:
        type === GovernanceActionType.UpdateToTheConstitution
          ? 'details'
          : 'parameters',
      label:
        type === GovernanceActionType.UpdateToTheConstitution
          ? 'Details'
          : 'Parameters',
    },
  ];

  const [activeTab, setActiveTab] = useState<ITabOption>(metadataTabs[0]);

  const TabComponent = ({ value }: { value: string }) => {
    switch (value) {
      case 'resoning':
        return (
          <GovernanceActionReasoning
            governanceAction={governanceAction}
            metadataResponse={metadataResponse}
          />
        );
      case 'details':
      case 'parameters':
        return (
          <GovernanceExtraActionDetailsPage
            governanceAction={governanceAction}
          />
        );
      default:
        null;
    }
  };

  const handleSetActiveTab = (index: number) => {
    setActiveTab(metadataTabs[index]);
  };

  useEffect(() => {
    const tab = readSearchParam('tab') || 'info';
    if (tab === 'reasoning') {
      return;
    }
    const tabs = metadataTabs.find((item) => item?.value === tab);
    tabs && setActiveTab(tabs);
  }, []);

  const CommonDetails = (
    <>
      {metadataUrl && (
        <div className="flex flex-col gap-1">
          <p className="body16 font-semibold dark:text-white">
            Metadata anchor link
          </p>
          <AnchorLink
            href={handleNavigationUrl(metadataUrl)}
            className="body14 min-w-0 max-w-[210px] truncate text-gray-400 hover:text-primary-500 sm:max-w-[300px] xl:max-w-[500px]"
            target="_blank"
          >
            {metadataUrl}
          </AnchorLink>
        </div>
      )}
      {metadataHash && (
        <GovActionMetadataCopiableDescriptionField
          label="Metadata anchor hash"
          value={metadataHash}
        />
      )}
      {metadataResponse?.metadata && metadataResponse.metadata.references && (
        <div className="flex flex-col gap-1">
          <p className="body16 font-semibold dark:text-white">
            Supporting links
          </p>
          {metadataResponse.metadata.references.map((reference) => (
            <React.Fragment key={getFieldValue(reference.uri)}>
              <div className="body14 flex items-center gap-2">
                <p className="dark:text-white">
                  {getFieldValue(reference.label)}
                </p>
                <AnchorLink
                  href={handleNavigationUrl(getFieldValue(reference.uri))}
                  className="body14 min-w-0 max-w-[210px] truncate text-gray-400 hover:text-primary-500 sm:max-w-[300px] xl:max-w-[500px]"
                  target="_blank"
                >
                  {getFieldValue(reference.uri)}
                </AnchorLink>
              </div>
            </React.Fragment>
          ))}
        </div>
      )}
    </>
  );

  if (
    type === GovernanceActionType.UpdateToTheConstitution ||
    type === GovernanceActionType.ProtocolParameterChange ||
    type === GovernanceActionType.NewCommittee
  )
    return (
      <div className="w-full max-w-full overflow-x-auto rounded-xl border dark:border-dark-neutral-700 dark:bg-dark-neutral-900">
        <Tabs
          className="border-b border-gray-200 dark:border-dark-neutral-700"
          items={metadataTabs}
          onChange={handleSetActiveTab}
          currentActiveIndex={metadataTabs.findIndex(
            (tab) => tab.value === activeTab.value
          )}
        />
        <ContentCard className="relative flex w-full flex-col !overflow-hidden border border-dark-neutral-700 bg-dark-neutral-900">
          <div className="flex flex-col gap-4 overflow-x-auto">
            <TabComponent value={activeTab.value} />
            {CommonDetails}
          </div>
        </ContentCard>
      </div>
    );
  return (
    <ContentCard className="relative flex w-full flex-col !overflow-hidden rounded-xl border border-dark-neutral-700 bg-dark-neutral-900">
      <div className="flex flex-col gap-4">
        <GovernanceActionReasoning
          governanceAction={governanceAction}
          metadataResponse={metadataResponse}
        />
        {CommonDetails}
      </div>
    </ContentCard>
  );
};
export default GovernanceActionMetadata;
